import { useState } from 'react';
import { Box, Link as MUILink, Skeleton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { MdOutlineTune, MdOutlineWifi, MdOutlineWifiOff } from 'react-icons/md';
import { HiOutlineLightningBolt } from 'react-icons/hi';
import Button from 'components/Common/Button';
import { useGetThirdPartyIntegrations, useToggleThirdPartyIntegration } from 'apis/agency/thirdPartyIntegrations/hook';
import { ThirdPartyIntegrationState } from 'apis/agency/thirdPartyIntegrations/types';
import { getProviderLogo } from './helpers';
import ThirdPartyIntegrationDrawer from './components/ThirdPartyIntegrationDrawer';
import DocumentationIcon from '@/assets/icons/DocumentationIcon';

function IntegrationCard({
  state,
  onConfigure
}: {
  state: ThirdPartyIntegrationState;
  onConfigure: (state: ThirdPartyIntegrationState) => void;
}) {
  const { mutate: toggleIntegration, isPending: isToggling } = useToggleThirdPartyIntegration(state.provider);
  const isActivated = state.credentials_present;
  const isConnected = state.active;
  const disableToggle = state?.provider === 'stripe' && state?.active;

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    toggleIntegration({ enabled: event.target.checked });
  };

  const switchControl = (
    <Switch
      checked={state.enabled}
      onChange={handleToggle}
      disabled={isToggling || disableToggle}
      disableRipple
      sx={{
        '& .MuiSwitch-track': {
          height: 14,
          borderRadius: 7,
          backgroundColor: '#E0E0E0',
          opacity: 1,
          transition: 'background-color 250ms'
        }
      }}
    />
  );

  return (
    <Box
      bgcolor='#fff'
      border='1px solid #ECECEC'
      borderRadius={2}
      p={2.5}
      display='flex'
      flexDirection='column'
      height='100%'
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        {getProviderLogo(state.provider)}
        {isActivated && (
          <Stack direction='row' alignItems='center' gap={1}>
            {disableToggle ? (
              <Tooltip title='If you want to disable this, contact support.' placement='top' arrow>
                <Box component='span' sx={{ display: 'inline-flex' }}>
                  {switchControl}
                </Box>
              </Tooltip>
            ) : (
              switchControl
            )}
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              sx={{
                color: isConnected ? '#16A34A' : '#F59E0B',
                fontSize: 22,
                lineHeight: 0
              }}
            >
              {isConnected ? <MdOutlineWifi /> : <MdOutlineWifiOff />}
            </Box>
          </Stack>
        )}
      </Stack>

      <Typography mt={1.5} color='secondary' fontSize={12} lineHeight={1.45}>
        {state.description}
      </Typography>

      <Stack direction='row' alignItems='center' justifyContent='space-between' mt={2.5} flexWrap='wrap' gap={1}>
        <MUILink
          href={state.documentation_url}
          target='_blank'
          rel='noopener'
          underline='none'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'primary.main',
            fontSize: 14,
            fontWeight: 500
          }}
        >
          <DocumentationIcon />
          View Documentation
        </MUILink>

        {isActivated ? (
          <Button
            variant='outlined'
            onClick={() => onConfigure(state)}
            sx={{
              borderRadius: '999px',
              px: 2.5,
              py: 0.8,
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.main' }
            }}
            startIcon={<MdOutlineTune />}
          >
            Change Configuration
          </Button>
        ) : (
          <Button
            variant='contained'
            onClick={() => onConfigure(state)}
            sx={{
              borderRadius: '999px',
              px: 3,
              py: 0.8,
              textTransform: 'none',
              fontWeight: 500
            }}
            startIcon={<HiOutlineLightningBolt />}
          >
            Activate
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function CardSkeleton() {
  return (
    <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2.5}>
      <Skeleton variant='text' width={120} height={28} />
      <Skeleton variant='text' width='90%' height={20} sx={{ mt: 1 }} />
      <Skeleton variant='text' width='70%' height={20} />
      <Stack direction='row' justifyContent='space-between' alignItems='center' mt={2.5}>
        <Skeleton variant='text' width={140} height={20} />
        <Skeleton variant='rectangular' width={120} height={36} sx={{ borderRadius: '999px' }} />
      </Stack>
    </Box>
  );
}

export default function ThirdParty() {
  const { data: integrations = [], isLoading } = useGetThirdPartyIntegrations();
  const [activeState, setActiveState] = useState<ThirdPartyIntegrationState | null>(null);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' }, fontWeight: 500 }}>
        Third Party Integrations
      </Typography>
      <Typography color='secondary' variant='body2' mt={0.5} sx={{ fontSize: { xs: '14px', lg: 16 } }}>
        Activate external services to extend your agency. Each integration has its own activation flow.
      </Typography>

      <Grid container spacing={2} mt={4}>
        {isLoading ? (
          Array.from({ length: 2 }).map((_, idx) => (
            <Grid key={idx} size={{ xs: 12, md: 6 }}>
              <CardSkeleton />
            </Grid>
          ))
        ) : integrations.length ? (
          integrations.map((state) => (
            <Grid key={state.provider} size={{ xs: 12, md: 6 }}>
              <IntegrationCard state={state} onConfigure={setActiveState} />
            </Grid>
          ))
        ) : (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            border='1px solid #ECECEC'
            borderRadius={1}
            padding={2}
            width='100%'
            height='100px'
            mt={2}
          >
            <Typography color='secondary.dark'>No Integration Found.</Typography>
          </Box>
        )}
      </Grid>

      <ThirdPartyIntegrationDrawer
        open={Boolean(activeState)}
        onClose={() => setActiveState(null)}
        state={activeState}
      />
    </Box>
  );
}
